if (!customElements.get('ember-creator-videos')) {
  customElements.define('ember-creator-videos', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const rail = this.querySelector('[data-creator-rail]');
      if (!rail) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      const videos = [...this.querySelectorAll('video')];
      const visibleVideos = new Set();
      const firstVideo = videos[0];
      let hasInteracted = false;
      const manuallyPaused = new Set();
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      const connection = navigator.connection;
      const canAutoplay = () => !reducedMotion.matches && !connection?.saveData;
      const previous = this.querySelector('[data-creator-previous]');
      const next = this.querySelector('[data-creator-next]');
      const navigation = this.querySelector('[data-creator-navigation]');
      const updateNavigation = () => {
        if (!navigation) return;
        const maxScroll = rail.scrollWidth - rail.clientWidth;
        navigation.hidden = maxScroll <= 2;
        previous.disabled = rail.scrollLeft <= 2;
        next.disabled = rail.scrollLeft >= maxScroll - 2;
      };
      const advance = (direction) => {
        const card = rail.querySelector('.ember-cv__card');
        if (!card) return;
        const distance = card.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).columnGap || 0);
        rail.scrollBy({ left: direction * distance, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      };
      const play = (video) => {
        const result = video.play();
        if (result?.catch) result.catch(() => {});
      };
      const autoplay = (video) => {
        if (video !== firstVideo || hasInteracted || !canAutoplay() || document.hidden || manuallyPaused.has(video) || !visibleVideos.has(video)) return;
        if ([...document.querySelectorAll('ember-creator-videos video')].some((other) => other !== video && !other.paused)) return;
        video.muted = true;
        play(video);
      };
      previous?.addEventListener('click', () => advance(-1), { signal });
      next?.addEventListener('click', () => advance(1), { signal });
      rail.addEventListener('scroll', updateNavigation, { passive: true, signal });
      rail.addEventListener('keydown', (event) => {
        if (event.target !== rail || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        advance(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      videos.forEach((video) => {
        const card = video.closest('.ember-cv__card');
        const togglePlay = card.querySelector('[data-creator-play]');
        const toggleSound = card.querySelector('[data-creator-sound]');
        const creator = video.getAttribute('aria-label') || 'this creator';
        const updateControls = () => {
          togglePlay.setAttribute('aria-pressed', String(!video.paused));
          togglePlay.setAttribute('aria-label', `${video.paused ? 'Play' : 'Pause'} video by ${creator}`);
          toggleSound.setAttribute('aria-pressed', String(!video.muted));
          toggleSound.setAttribute('aria-label', `${video.muted ? 'Turn sound on for' : 'Mute video by'} ${creator}`);
        };
        const muteOtherVideos = () => {
          document.querySelectorAll('ember-creator-videos video').forEach((other) => {
            if (other !== video) other.muted = true;
          });
        };
        video.muted = true;
        video.controls = false;
        ['play', 'pause', 'volumechange', 'ended', 'error'].forEach((event) => video.addEventListener(event, updateControls, { signal }));
        video.addEventListener('play', () => {
          document.querySelectorAll('ember-creator-videos video').forEach((other) => {
            if (other !== video) { other.pause(); other.muted = true; }
          });
        }, { signal });
        video.addEventListener('volumechange', () => { if (!video.muted) muteOtherVideos(); }, { signal });
        togglePlay.addEventListener('click', () => {
          hasInteracted = true;
          if (video.paused) {
            manuallyPaused.delete(video);
            play(video);
          } else {
            manuallyPaused.add(video);
            video.pause();
          }
        }, { signal });
        toggleSound.addEventListener('click', () => {
          hasInteracted = true;
          video.muted = !video.muted;
          if (!video.muted) {
            muteOtherVideos();
            manuallyPaused.delete(video);
            if (video.paused) play(video);
          }
        }, { signal });
        updateControls();
      });
      this.dataset.enhanced = 'true';
      document.addEventListener('visibilitychange', () => {
        videos.forEach((video) => {
          if (document.hidden) { video.pause(); video.muted = true; }
          else autoplay(video);
        });
      }, { signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
              visibleVideos.add(video);
              autoplay(video);
            } else {
              visibleVideos.delete(video);
              video.pause();
              video.muted = true;
            }
          });
        }, { threshold: [0, 0.35] });
        videos.forEach((video) => this.observer.observe(video));
      }
      const updateAutoplayPreference = () => {
        videos.forEach((video) => { if (canAutoplay()) autoplay(video); else video.pause(); });
      };
      reducedMotion.addEventListener('change', updateAutoplayPreference, { signal });
      connection?.addEventListener('change', updateAutoplayPreference, { signal });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(updateNavigation);
        this.resizeObserver.observe(rail);
      } else {
        window.addEventListener('resize', updateNavigation, { signal });
      }
      updateNavigation();
    }
    disconnectedCallback() {
      this.querySelectorAll('video').forEach((video) => video.pause());
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }
  });
}
